import React, { ReactElement, useState } from 'react';
import { Linking, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';

import { useIsPortrait } from 'components/ProvidersContainer/ScreenOrientationProvider';
import { NHSLoginButton } from 'components/Screens/LoginScreen/components/OIDCLogin';
import { Text } from 'components/Text';
import { useHistory, useParams } from 'cross-platform/react-router';

import {
  NHS_LOGIN_CONTACT_EMAIL,
  NHS_LOGIN_CONTACT_EMAIL_ADDRESS,
  NHS_LOGIN_CONTACT_PREFIX,
  NHS_LOGIN_EMAIL_PATH_LINK,
  NHS_LOGIN_EMAIL_PATH_PREFIX,
  NHS_LOGIN_SUBTITLE,
  NHS_LOGIN_TITLE,
  NHS_LOGIN_TROUBLE,
} from '../constants';
import { NHSBackButton } from '../NHSBackButton';
import { NHSCloseButton } from '../NHSCloseButton';
import { NHSScrollWrapper } from '../styled';

import { ThumbsUpIllustration } from './ThumbsUpIllustration';

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: ${({ theme }) => theme.color.white};
  padding-left: 24px;
  padding-right: 24px;
`;

const IllustrationContainer = styled(View)`
  margin-bottom: 24px;
`;

const Title = styled<typeof Text>(Text)`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.overlay.primary};
  text-align: center;
  margin-bottom: 12px;
`;

const Subtitle = styled<typeof Text>(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.overlay.primary};
  text-align: center;
  margin-bottom: 24px;
  line-height: 20px;
`;

const InlineText = styled<typeof Text>(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.overlay.primary};
  text-align: center;
  margin-top: 16px;
`;

const InlineLink = styled<typeof Text>(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.midBlue};
  text-decoration-line: underline;
`;

const TroubleText = styled<typeof Text>(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.overlay.primary};
  text-align: center;
  margin-top: 32px;
`;

const ContactRow = styled<typeof Text>(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.overlay.primary};
  text-align: center;
  margin-top: 4px;
`;

export const NHSLoginScreen = (): ReactElement => {
  const history = useHistory();
  const { productReference } = useParams<{ productReference: string }>();
  const isPortrait = useIsPortrait();
  const [isNHSWebViewVisible, setIsNHSWebViewVisible] = useState(false);

  return (
    <Container>
      <NHSScrollWrapper
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingVertical: isPortrait ? 0 : 32,
        }}
      >
        <IllustrationContainer>
          <ThumbsUpIllustration size={160} />
        </IllustrationContainer>
        <Title>{NHS_LOGIN_TITLE}</Title>
        <Subtitle>{NHS_LOGIN_SUBTITLE}</Subtitle>
        <NHSLoginButton onWebViewVisibilityChange={setIsNHSWebViewVisible} />
        <InlineText>
          {NHS_LOGIN_EMAIL_PATH_PREFIX}
          <InlineLink
            onPress={() =>
              history.push(`/${productReference}/login-screen`, {
                hideNHSButton: true,
              })
            }
          >
            {NHS_LOGIN_EMAIL_PATH_LINK}
          </InlineLink>
        </InlineText>
        <TroubleText>{NHS_LOGIN_TROUBLE}</TroubleText>
        <ContactRow>
          {NHS_LOGIN_CONTACT_PREFIX}
          <InlineLink onPress={() => Linking.openURL(NHS_LOGIN_CONTACT_EMAIL)}>
            {NHS_LOGIN_CONTACT_EMAIL_ADDRESS}
          </InlineLink>
        </ContactRow>
      </NHSScrollWrapper>
      {!isNHSWebViewVisible && (
        <>
          <NHSBackButton onPress={() => history.goBack()} />
          <NHSCloseButton
            onPress={() => history.push(`/${productReference}/login`)}
          />
        </>
      )}
    </Container>
  );
};

export default NHSLoginScreen;
